// import your icons
import referralBonus from "@/assets/dashboard/stats-icon/referral-bonus.svg";
import totalCashouts from "@/assets/dashboard/stats-icon/total-cashout.svg";
import totalDeposits from "@/assets/dashboard/stats-icon/total-deposits.svg";
import totalPayments from "@/assets/dashboard/stats-icon/total-payment.svg";
import totalReferrals from "@/assets/dashboard/stats-icon/total-referrals.svg";
import totalTickets from "@/assets/dashboard/stats-icon/total-tickets.svg";
import totalTransactions from "@/assets/dashboard/stats-icon/total-transaction.svg";
import totalTransfers from "@/assets/dashboard/stats-icon/total-transfer.svg";
import totalWallets from "@/assets/dashboard/stats-icon/total-wallets.svg";
import totalWithdraws from "@/assets/dashboard/stats-icon/total-withdraws.svg";

export function formatTitle(path) {
  if (!path) return "Dashboard";

  const parts = path.split("/").filter(Boolean);
  const lastSegment = parts[parts.length - 1];

  if (lastSegment === "dashboard") return "Dashboard";

  return lastSegment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// calculate min and max amount helper function
export const dynamicDecimals = ({
  currencyCode,
  siteCurrencyCode,
  siteCurrencyDecimals,
  currencyDecimals,
}) => {
  const parsedCurrencyDecimals = Number(currencyDecimals);
  const parsedSiteCurrencyDecimals = Number(siteCurrencyDecimals);
  const dynamicCurrency = currencyCode === siteCurrencyCode;

  if (Number.isFinite(parsedCurrencyDecimals)) {
    return parsedCurrencyDecimals;
  }

  if (dynamicCurrency && Number.isFinite(parsedSiteCurrencyDecimals)) {
    return parsedSiteCurrencyDecimals;
  }

  return 2;
};

// calculate charge helper function
export const calculateCharge = (gatewayMethod, siteCurrency) => {
  if (!gatewayMethod) return { mainCharge: "", displayChargeType: "" };

  const gatewayCurrency = gatewayMethod?.currency;
  const gatewayCharge = gatewayMethod?.charge;
  const chargeType = gatewayMethod?.charge_type;
  const decimals = dynamicDecimals({
    currencyCode: gatewayCurrency,
    siteCurrencyCode: siteCurrency,
    currencyDecimals: gatewayMethod?.currency_decimals,
    siteCurrencyDecimals: 2,
  });

  const mainCharge = Number(gatewayCharge).toFixed(decimals);

  let displayChargeType = chargeType === "percentage" ? "%" : gatewayCurrency;

  return { mainCharge, displayChargeType };
};

// calculate min and max amount helper function
export const calculateMinMaxAmount = (gatewayMethod, siteCurrency) => {
  if (!gatewayMethod) return { minAmount: "", maxAmount: "" };

  const gatewayCurrency = gatewayMethod?.currency;
  const gatewayMinCurrency = gatewayMethod?.minimum_deposit;
  const gatewayMaxCurrency = gatewayMethod?.maximum_deposit;
  const decimals = dynamicDecimals({
    currencyCode: gatewayCurrency,
    siteCurrencyCode: siteCurrency,
    currencyDecimals: gatewayMethod?.currency_decimals,
    siteCurrencyDecimals: 2,
  });

  const minAmount = Number(gatewayMinCurrency).toFixed(decimals);
  const maxAmount = Number(gatewayMaxCurrency).toFixed(decimals);

  return { minAmount, maxAmount };
};

// get settings value
export const getSettingValue = (settings, key) => {
  if (!settings?.data) return null;
  const found = settings.data.find((item) => item.name === key);
  return found ? found.value : null;
};

//  get settings value (server)
export const getSetting = (list, key) =>
  list?.find((item) => item.name === key)?.value;

//get field status
export const getFieldStatus = (fields, key) => {
  if (!Array.isArray(fields)) return false;
  const found = fields.find((item) => item.key === key);
  return found?.value === "1";
};

//formate date created at
export function CreatedAtFormatDate({ createdAt }) {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (isNaN(date)) return null;

  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const formatted = new Intl.DateTimeFormat("en-US", options)
    .format(date)
    .replace(",", "");

  return <span>{formatted}</span>;
}

//formate local date
export const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

//parse text color
export const parseColorTextToJSX = (text) => {
  const pattern = /\[\[color_text=\s*(.*?)\s*\]\]/;
  const match = text.match(pattern);
  if (!match) return text;
  const coloredWord = match[1];
  const cleanedText = text.replace(pattern, "__COLOR__");
  const [before, after] = cleanedText.split("__COLOR__");

  return (
    <>
      {before}
      <span className="text-user-primary">{coloredWord}</span>
      {after}
    </>
  );
};

export const splitColorText = (text) => {
  if (!text || typeof text !== "string") {
    return { title: "", highlightTitle: "" };
  }
  const pattern = /\[\[color_text=\s*(.*?)\s*\]\]/;
  const match = text.match(pattern);
  if (!match) return { title: text, highlightTitle: "" };
  const highlightTitle = match[1];
  const title = text.replace(pattern, "").trim();
  return { title, highlightTitle };
};

export const stripHtml = (htmlString = "") => {
  const div = document.createElement("div");

  div.innerHTML = htmlString;
  const decodedOnce = div.textContent || div.innerText || "";

  div.innerHTML = decodedOnce;
  const decodedTwice = div.textContent || div.innerText || "";

  return decodedTwice
    .replace(/<\/(p|h1|h2|h3|h4|h5|h6|li|article|section|div|br)>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const normalizeHref = (slug) => {
  if (!slug) return "/";

  if (slug.startsWith("http") || slug.startsWith("https")) return slug;

  return slug.startsWith("/") ? slug : `/${slug}`;
};

//decode html
export const fixImageUrls = (html = "") => {
  return html.replace(
    /<img\s+[^>]*src="(\/[^"]+)"/g,
    `<img src="${process.env.NEXT_PUBLIC_API_BASE_URL}$1"`,
  );
};

export const decodeHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

// get user transaction icon
export const getUserTransactionIcon = (type) => {
  switch (type) {
    case "Deposit":
      return {
        icon: "fi fi-rr-money-bill-simple",
        bg: "bg-[#9280FD]",
      };
    case "Manual Deposit":
      return {
        icon: "fi fi-rr-loan",
        bg: "bg-[#CC66DA]",
      };
    case "Credit":
      return {
        icon: "fi fi-rr-arrow-small-up",
        bg: "bg-[#95BC25]",
      };
    case "Cash In":
      return {
        icon: "fi fi-rr-arrow-small-left",
        bg: "bg-[#2C5671]",
      };
    case "Cash Received":
      return {
        icon: "fi fi-rr-wallet",
        bg: "bg-[#689B8A]",
      };
    case "Receive Money":
      return {
        icon: "fi fi-rr-piggy-bank",
        bg: "bg-[#95BC25]",
      };
    case "Withdraw":
      return {
        icon: "fi fi-rr-arrow-small-down",
        bg: "bg-[#00B2FF]",
      };
    case "Withdraw Auto":
      return {
        icon: "fi fi-rr-bolt",
        bg: "bg-[#437D34]",
      };
    case "Cash Out":
      return {
        icon: "fi fi-rr-sack",
        bg: "bg-[#EC4963]",
      };
    case "Send Money":
      return {
        icon: "fi fi-rr-paper-plane",
        bg: "bg-[#615CFF]",
      };
    case "Debit":
      return {
        icon: "fi fi-rr-cards-blank",
        bg: "bg-[#A6B28B]",
      };
    case "Payment":
      return {
        icon: "fi fi-rr-wallet-arrow",
        bg: "bg-[#3AB83A]",
      };
    case "Exchange":
      return {
        icon: "fi fi-rr-exchange",
        bg: "bg-[#15916C]",
      };
    case "Gift Card":
      return {
        icon: "fi fi-rr-basket-shopping-simple",
        bg: "bg-[#797979]",
      };
    case "Gift Redeemed":
      return {
        icon: "fi fi-rr-box-heart",
        bg: "bg-[#A282FF]",
      };
    case "Referral":
      return {
        icon: "fi fi-rr-referral",
        bg: "bg-[#087070]",
      };
    case "Signup Bonus":
      return {
        icon: "fi fi-rr-bonus-alt",
        bg: "bg-[#3AAADA]",
      };
    case "Refund":
      return {
        icon: "fi fi-rr-refund",
        bg: "bg-[#B75931]",
      };
    case "Request Money":
      return {
        icon: "fi fi-rr-budget-alt",
        bg: "bg-[#FFAA00]",
      };
    case "Invoice":
      return {
        icon: "fi fi-rr-receipt",
        bg: "bg-[#78C841]",
      };
    default:
      return {
        icon: "fi fi-rr-bank",
        bg: "bg-[#6B7280]",
      };
  }
};

export const getStatStyle = (type) => {
  switch (type) {
    case "total-deposits":
      return {
        bg: "bg-[rgba(255,215,251,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalDeposits,
      };
    case "total-wallets":
      return {
        bg: "bg-[rgba(189,255,255,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalWallets,
      };
    case "total-cashouts":
      return {
        bg: "bg-[rgba(223,224,255,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalCashouts,
      };
    case "total-withdraws":
      return {
        bg: "bg-[rgba(255,233,200,0.30))]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalWithdraws,
      };
    case "total-payments":
      return {
        bg: "bg-[rgba(255,225,216,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalPayments,
      };
    case "total-transfers":
      return {
        bg: "bg-[rgba(228,228,228,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalTransfers,
      };
    case "referral-bonus":
      return {
        bg: "bg-[rgba(235,215,255,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: referralBonus,
      };
    case "total-referrals":
      return {
        bg: "bg-[rgba(249,255,183,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalReferrals,
      };
    case "total-transactions":
      return {
        bg: "bg-[rgba(200,255,224,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalTransactions,
      };
    case "total-tickets":
      return {
        bg: "bg-[rgba(187,220,229,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalTickets,
      };
    default:
      return {
        bg: "bg-[rgba(255,255,255,0.30)]",
        iconBg: "bg-[rgba(117,69,255,0.10)]",
        icon: totalDeposits,
      };
  }
};

//1st row 3 item 2nd row 3 item 3rd row 3 item chunk
export const chunkData = (items, size = 3) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};
